<!-- mettal-3\src\views\auth\Register.vue -->
<template>
  <div class="register">
    <!-- Background -->
    <div class="register__bg">
      <div class="register__bg-circle register__bg-circle--1"></div>
      <div class="register__bg-circle register__bg-circle--2"></div>
      <div class="register__bg-dots"></div>
    </div>

    <div class="register__layout">
      <!-- Left Side - Branding -->
      <div class="register__branding">
        <div class="register__branding-content">
          <h1 class="register__logo">МеталлТрейд Академия</h1>

          <p class="register__tagline">
            Начните свой путь к новым знаниям и навыкам уже сегодня
          </p>

          <div class="register__stats">
            <div class="register__stat">
              <span class="register__stat-value">10K+</span>
              <span class="register__stat-label">Студентов</span>
            </div>
            <div class="register__stat-divider"></div>
            <div class="register__stat">
              <span class="register__stat-value">500+</span>
              <span class="register__stat-label">Курсов</span>
            </div>
            <div class="register__stat-divider"></div>
            <div class="register__stat">
              <span class="register__stat-value">98%</span>
              <span class="register__stat-label">Довольны</span>
            </div>
          </div>

          <div class="register__features">
            <div class="register__feature" v-for="(feature, index) in features" :key="index">
              <div class="register__feature-icon" v-html="feature.icon"></div>
              <div class="register__feature-text">
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
              </div>
            </div>
          </div>

          <div class="register__testimonial">
            <p class="register__testimonial-text">
              "Благодаря МеталлТрейд Академии я смог освоить новую профессию за 3 месяца!"
            </p>
            <div class="register__testimonial-author">
              <div class="register__testimonial-avatar">АК</div>
              <div>
                <strong>Александр Козлов</strong>
                <span>Web-разработчик</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Decorative rings -->
        <div class="register__branding-ring register__branding-ring--1"></div>
        <div class="register__branding-ring register__branding-ring--2"></div>
      </div>

      <!-- Right Side - Registration Form -->
      <div class="register__form-wrapper">
        <div class="register__form-container">
          <!-- Режим обслуживания -->
          <div v-if="maintenanceMode" class="register__state">
            <div class="register__state-icon register__state-icon--maintenance">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </div>
            <h2>Технические работы</h2>
            <p>Регистрация временно недоступна. Мы скоро вернёмся!</p>
            <router-link to="/login" class="register__state-link">
              <span>Перейти ко входу</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </router-link>
          </div>

          <!-- Регистрация отключена -->
          <div v-else-if="!allowRegistration && !inviteToken" class="register__state">
            <div class="register__state-icon register__state-icon--locked">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2>Регистрация закрыта</h2>
            <p>Регистрация новых пользователей временно отключена.</p>
            <p v-if="!inviteToken" class="register__state-hint">Для регистрации необходимо приглашение</p>
            <router-link to="/login" class="register__state-link">
              <span>Перейти ко входу</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </router-link>
          </div>

          <!-- Форма регистрации -->
          <template v-else>
            <div class="register__header">
              <div v-if="inviteData" class="register__invite-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Приглашение подтверждено</span>
              </div>
              <h2 class="register__title">{{ inviteData ? 'Добро пожаловать!' : 'Создать аккаунт' }}</h2>
              <p class="register__subtitle">
                <template v-if="inviteData">
                  Вы приглашены как <strong>{{ getRoleName(inviteData.role) }}</strong>
                </template>
              </p>
            </div>

            <!-- Progress -->
            <div class="register__progress">
              <div class="register__progress-track">
                <div class="register__progress-bar" :style="{ width: formProgress + '%' }"></div>
              </div>
              <span class="register__progress-text">{{ formProgress }}%</span>
            </div>

            <form @submit.prevent="handleRegister" class="register__form">
              <!-- Name Row -->
              <div class="form-row">
                <div class="form-group" :class="{ 'form-group--filled': formData.lastName, 'form-group--focused': focusedField === 'lastName', 'form-group--disabled': inviteBlocked }">
                  <div class="form-group__input-wrapper">
                    <input id="lastName" v-model="formData.lastName" type="text" class="form-group__input" placeholder=" " required :disabled="inviteBlocked" @focus="focusedField = 'lastName'" @blur="focusedField = null" />
                    <label for="lastName" class="form-group__label">Фамилия <span class="required">*</span></label>
                  </div>
                </div>
                <div class="form-group" :class="{ 'form-group--filled': formData.firstName, 'form-group--focused': focusedField === 'firstName', 'form-group--disabled': inviteBlocked }">
                  <div class="form-group__input-wrapper">
                    <input id="firstName" v-model="formData.firstName" type="text" class="form-group__input" placeholder=" " required :disabled="inviteBlocked" @focus="focusedField = 'firstName'" @blur="focusedField = null" />
                    <label for="firstName" class="form-group__label">Имя <span class="required">*</span></label>
                  </div>
                </div>
              </div>

              <!-- Отчество -->
              <div class="form-group" :class="{ 'form-group--filled': formData.middleName, 'form-group--focused': focusedField === 'middleName', 'form-group--disabled': inviteBlocked }">
                <div class="form-group__input-wrapper">
                  <input id="middleName" v-model="formData.middleName" type="text" class="form-group__input" placeholder=" " :disabled="inviteBlocked" @focus="focusedField = 'middleName'" @blur="focusedField = null" />
                  <label for="middleName" class="form-group__label">Отчество <span class="optional">(необязательно)</span></label>
                </div>
              </div>

              <!-- Email -->
              <div class="form-group" :class="{ 'form-group--filled': formData.email, 'form-group--focused': focusedField === 'email', 'form-group--readonly': inviteData?.email, 'form-group--disabled': inviteBlocked }">
                <div class="form-group__input-wrapper">
                  <input id="email" v-model="formData.email" type="email" class="form-group__input form-group__input--with-icon" placeholder=" " :readonly="inviteData?.email" :disabled="inviteBlocked" required @focus="focusedField = 'email'" @blur="focusedField = null" />
                  <label for="email" class="form-group__label">Email <span class="required">*</span></label>
                  <div class="form-group__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="4" width="20" height="16" rx="3"/><path d="M22 7l-10 7L2 7"/></svg>
                  </div>
                </div>
                <div v-if="inviteData?.email" class="form-group__hint form-group__hint--info">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                  Email привязан к приглашению
                </div>
              </div>

              <!-- Password -->
              <div class="form-group" :class="{ 'form-group--filled': formData.password, 'form-group--focused': focusedField === 'password', 'form-group--disabled': inviteBlocked }">
                <div class="form-group__input-wrapper">
                  <input id="password" v-model="formData.password" :type="showPassword ? 'text' : 'password'" class="form-group__input" placeholder=" " required :disabled="inviteBlocked" @focus="focusedField = 'password'" @blur="focusedField = null" />
                  <label for="password" class="form-group__label">Пароль <span class="required">*</span></label>
                  <button type="button" class="form-group__toggle" @click="showPassword = !showPassword" tabindex="-1">
                    <svg v-if="showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </div>

                <div v-if="formData.password" class="password-strength">
                  <div class="password-strength__bars">
                    <div v-for="i in 4" :key="i" class="password-strength__bar" :class="{ 'password-strength__bar--active': i <= passwordStrength.score }" :style="{ backgroundColor: i <= passwordStrength.score ? passwordStrength.color : '' }"></div>
                  </div>
                  <span class="password-strength__text" :style="{ color: passwordStrength.color }">{{ passwordStrength.text }}</span>
                </div>

                <div class="password-requirements">
                  <div class="password-requirement" :class="{ 'password-requirement--met': formData.password.length >= 8 }">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span>Минимум 8 символов</span>
                  </div>
                  <div class="password-requirement" :class="{ 'password-requirement--met': /[A-Za-z]/.test(formData.password) }">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span>Содержит буквы</span>
                  </div>
                  <div class="password-requirement" :class="{ 'password-requirement--met': /[0-9]/.test(formData.password) }">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span>Содержит цифры</span>
                  </div>
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="form-group" :class="{
                'form-group--filled': formData.confirmPassword,
                'form-group--focused': focusedField === 'confirmPassword',
                'form-group--error': formData.confirmPassword && formData.password !== formData.confirmPassword,
                'form-group--success': formData.confirmPassword && formData.password === formData.confirmPassword,
                'form-group--disabled': inviteBlocked,
              }">
                <div class="form-group__input-wrapper">
                  <input id="confirmPassword" v-model="formData.confirmPassword" :type="showPassword ? 'text' : 'password'" class="form-group__input" placeholder=" " required :disabled="inviteBlocked" @focus="focusedField = 'confirmPassword'" @blur="focusedField = null" />
                  <label for="confirmPassword" class="form-group__label">Подтвердите пароль <span class="required">*</span></label>
                  <div v-if="formData.confirmPassword" class="form-group__status">
                    <svg v-if="formData.password === formData.confirmPassword" class="form-group__status--success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <svg v-else class="form-group__status--error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  </div>
                </div>
                <div v-if="formData.confirmPassword && formData.password !== formData.confirmPassword" class="form-group__hint form-group__hint--error">
                  Пароли не совпадают
                </div>
              </div>

              <!-- Роль -->
              <div v-if="inviteData" class="register__role-card">
                <div class="register__role-icon">
                  <svg v-if="inviteData.role === 'teacher'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                </div>
                <div class="register__role-info">
                  <span class="register__role-label">Ваша роль</span>
                  <strong class="register__role-name">{{ getRoleName(inviteData.role) }}</strong>
                </div>
              </div>

              <!-- Terms -->
              <label class="checkbox" :class="{ 'checkbox--disabled': inviteBlocked }">
                <input v-model="formData.agreeToTerms" type="checkbox" class="checkbox__input" required :disabled="inviteBlocked" />
                <span class="checkbox__box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <span class="checkbox__text">
                  Я согласен с
                  <a href="#" class="checkbox__link" @click.prevent>условиями использования</a>
                  и
                  <a href="#" class="checkbox__link" @click.prevent>политикой конфиденциальности</a>
                </span>
              </label>

              <!-- Error -->
              <transition name="shake">
                <div v-if="error" class="register__error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{{ error }}</span>
                </div>
              </transition>

              <!-- Submit -->
              <button type="submit" class="register__submit" :disabled="!canSubmit" :class="{ 'register__submit--loading': loading }">
                <span class="register__submit-content">
                  <span v-if="!loading">Создать аккаунт</span>
                  <span v-else>Создание аккаунта...</span>
                </span>
                <div v-if="loading" class="register__submit-spinner"><div></div><div></div><div></div></div>
                <svg v-else class="register__submit-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </form>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authService } from "@/services/auth";
import invitesService from "@/services/invites";

export default {
  name: "Register",
  data() {
    return {
      formData: {
        firstName: "",
        lastName: "",
        middleName: "",
        email: "",
        password: "",
        confirmPassword: "",
        agreeToTerms: false,
      },
      showPassword: false,
      loading: false,
      error: "",
      focusedField: null,
      allowRegistration: true,
      maintenanceMode: false,
      settingsLoaded: false,
      inviteToken: null,
      inviteData: null,
      inviteError: null,
      inviteBlocked: false,
      features: [
        {
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke-width="1.5" fill="currentColor" fill-opacity="0.08"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke-width="1.5" fill="currentColor" fill-opacity="0.08"/>
            <line x1="6" y1="8" x2="8" y2="8" stroke-width="1.5" opacity="0.5"/>
            <line x1="6" y1="12" x2="9" y2="12" stroke-width="1.5" opacity="0.5"/>
            <line x1="15" y1="8" x2="18" y2="8" stroke-width="1.5" opacity="0.5"/>
            <line x1="15" y1="12" x2="17" y2="12" stroke-width="1.5" opacity="0.5"/>
          </svg>`,
          title: "Качественные курсы",
          description: "От лучших преподавателей отрасли",
        },
        {
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="9" r="6" stroke-width="1.5"/>
            <path d="M8.5 14.5L7 22l5-3 5 3-1.5-7.5" stroke-width="1.5"/>
            <polyline points="9.5 9 11 10.5 14.5 7" stroke-width="1.5" opacity="0.6"/>
          </svg>`,
          title: "Сертификаты",
          description: "Подтвердите свои знания официально",
        },
        {
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="7" r="4" stroke-width="1.5"/>
            <path d="M1 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" stroke-width="1.5"/>
            <circle cx="19" cy="7" r="3" stroke-width="1.5" opacity="0.45"/>
            <path d="M21 21v-2a3 3 0 0 0-2.5-2.96" stroke-width="1.5" opacity="0.45"/>
          </svg>`,
          title: "Сообщество",
          description: "Учитесь вместе с единомышленниками",
        },
      ],
    };
  },
  computed: {
    canSubmit() {
      return !this.inviteBlocked && !this.loading && this.formData.agreeToTerms && this.inviteData !== null;
    },
    formProgress() {
      if (this.inviteBlocked) return 0;
      let filled = 0;
      const total = 6;
      if (this.formData.lastName.trim()) filled++;
      if (this.formData.firstName.trim()) filled++;
      if (this.formData.email.trim()) filled++;
      if (this.formData.password.length >= 8) filled++;
      if (this.formData.confirmPassword && this.formData.password === this.formData.confirmPassword) filled++;
      if (this.formData.agreeToTerms) filled++;
      return Math.round((filled / total) * 100);
    },
    passwordStrength() {
      const password = this.formData.password;
      let score = 0;
      if (password.length >= 8) score++;
      if (password.length >= 10) score++;
      if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
      if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;
      const levels = [
        { score: 0, text: "", color: "#94a3b8" },
        { score: 1, text: "Слабый", color: "#ef4444" },
        { score: 2, text: "Средний", color: "#f59e0b" },
        { score: 3, text: "Хороший", color: "#10b981" },
        { score: 4, text: "Отличный", color: "#059669" },
      ];
      return levels[score] || levels[0];
    },
  },
  async created() {
    this.inviteToken = this.$route.query.invite || null;
    await Promise.all([
      this.loadPublicSettings(),
      this.inviteToken ? this.validateInvite() : Promise.resolve(),
    ]);
  },
  methods: {
    async loadPublicSettings() {
      try {
        const settings = await authService.getPublicSettings();
        this.allowRegistration = settings.allowRegistration;
        this.maintenanceMode = settings.maintenanceMode;
      } catch (err) {
        console.error("Ошибка загрузки настроек:", err);
        this.allowRegistration = true;
      }
      this.settingsLoaded = true;
    },
    async validateInvite() {
      if (!this.inviteToken) {
        this.inviteBlocked = true;
        this.inviteError = "Для регистрации необходимо приглашение";
        this.error = this.inviteError;
        return;
      }
      try {
        const result = await invitesService.validate(this.inviteToken);
        this.inviteData = result;
        this.inviteBlocked = false;
        if (result.email) this.formData.email = result.email;
      } catch (err) {
        this.inviteError = err.response?.data?.message || "Недействительное приглашение";
        this.error = this.inviteError;
        this.inviteToken = null;
        this.inviteBlocked = true;
        this.formData = { firstName: "", lastName: "", middleName: "", email: "", password: "", confirmPassword: "", agreeToTerms: false };
      }
    },
    getRoleName(role) {
      const names = { student: "Студент", teacher: "Преподаватель", admin: "Администратор" };
      return names[role] || role;
    },
    async handleRegister() {
      this.error = "";
      if (this.inviteBlocked) { this.error = "Регистрация заблокирована. Приглашение недействительно."; return; }
      if (this.inviteToken) { try { await invitesService.validate(this.inviteToken); } catch { this.inviteBlocked = true; this.error = "Приглашение уже использовано или недействительно"; return; } }
      if (!this.formData.lastName.trim()) { this.error = "Пожалуйста, введите фамилию"; return; }
      if (!this.formData.firstName.trim()) { this.error = "Пожалуйста, введите имя"; return; }
      if (!this.formData.email.trim()) { this.error = "Пожалуйста, введите email"; return; }
      if (this.formData.password.length < 8) { this.error = "Пароль должен содержать минимум 8 символов"; return; }
      if (this.formData.password !== this.formData.confirmPassword) { this.error = "Пароли не совпадают"; return; }
      if (!this.formData.agreeToTerms) { this.error = "Необходимо согласиться с условиями использования"; return; }
      this.loading = true;
      try {
        const response = await authService.register({
          email: this.formData.email, password: this.formData.password,
          first_name: this.formData.firstName, last_name: this.formData.lastName,
          middle_name: this.formData.middleName || null, invite_token: this.inviteToken || undefined,
        });
        if (response.data.requiresEmailVerification) { this.$router.push({ path: "/verify-email", query: { email: this.formData.email } }); return; }
        const user = response.data.user;
        const role = user.roles?.[0] || "student";
        let redirectPath = "/student-dashboard";
        if (role === "teacher") redirectPath = "/teacher/dashboard";
        else if (role === "admin") redirectPath = "/admin";
        this.$router.replace(redirectPath);
      } catch (error) {
        this.error = error.response?.data?.error?.message || error.response?.data?.message || "Ошибка при регистрации. Попробуйте снова.";
      } finally { this.loading = false; }
    },
  },
};
</script>

<style scoped>
/* ===================================================
   ПРОСТОЙ ПОДХОД: min-height вместо фиксированного height.
   Контент определяет высоту — страница просто скроллится
   если контент не влезает. Никакого обрезания.
   =================================================== */

* { box-sizing: border-box; margin: 0; padding: 0; }

/* ===== PAGE ===== */
.register {
  width: 100%;
  min-height: 100vh;       /* КЛЮЧЕВОЕ: min, а не фиксированный height */
  position: relative;
  background: #f0f7fd;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ===== BACKGROUND (fixed — не влияет на layout) ===== */
.register__bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(150deg, #eaf4fc 0%, #f0f7fd 40%, #f8fbff 100%);
}

.register__bg-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, #c8dfef 0.8px, transparent 0.8px);
  background-size: 28px 28px;
  opacity: 0.5;
}

.register__bg-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.06;
  background: #0096ed;
}

.register__bg-circle--1 {
  width: 600px;
  height: 600px;
  top: -250px;
  right: -150px;
}

.register__bg-circle--2 {
  width: 400px;
  height: 400px;
  bottom: -150px;
  left: -100px;
}

/* ===== LAYOUT ===== */
.register__layout {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 100vh;       /* Растягивается на весь экран... */
                            /* ...но РАСТЁТ если контент длиннее */
}

/* ===== BRANDING (LEFT) ===== */
.register__branding {
  width: 44%;
  flex-shrink: 0;
  background: linear-gradient(165deg, #007ad1 0%, #0096ed 50%, #2fb5ff 100%);
  color: #fff;
  display: flex;
  align-items: center;      /* Центрирует контент вертикально */
  padding: 48px 44px;
  position: relative;
  overflow: hidden;
}

.register__branding::after {
  content: "";
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.035'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2l2 3.25-2 3.25zM0 20h2v20H0V20z'/%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
}

.register__branding-content {
  position: relative;
  z-index: 1;
  width: 100%;
}

/* Decorative rings */
.register__branding-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.08);
  pointer-events: none;
}

.register__branding-ring--1 {
  width: 400px;
  height: 400px;
  bottom: -160px;
  right: -100px;
}

.register__branding-ring--2 {
  width: 250px;
  height: 250px;
  top: -60px;
  left: -50px;
}

/* Logo */
.register__logo {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.3px;
  margin-bottom: 10px;
}

.register__tagline {
  font-size: 15px;
  line-height: 1.6;
  opacity: 0.88;
  margin-bottom: 32px;
}

/* Stats */
.register__stats {
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 16px 0;
  margin-bottom: 32px;
  backdrop-filter: blur(8px);
}

.register__stat {
  flex: 1;
  text-align: center;
}

.register__stat-divider {
  width: 1px;
  height: 32px;
  background: rgba(255,255,255,0.18);
  flex-shrink: 0;
}

.register__stat-value {
  display: block;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
}

.register__stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.7;
}

/* Features */
.register__features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 28px;
}

.register__feature {
  display: flex;
  align-items: center;
  gap: 14px;
}

.register__feature-icon {
  width: 42px;
  height: 42px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s;
}

.register__feature:hover .register__feature-icon {
  background: rgba(255,255,255,0.2);
}

.register__feature-icon svg {
  width: 20px;
  height: 20px;
}

.register__feature-text h3 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 1px;
}

.register__feature-text p {
  font-size: 12px;
  opacity: 0.72;
}

/* Testimonial */
.register__testimonial {
  padding: 18px 20px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  border-left: 3px solid rgba(255,255,255,0.35);
}

.register__testimonial-text {
  font-size: 13px;
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 12px;
  opacity: 0.9;
}

.register__testimonial-author {
  display: flex;
  align-items: center;
  gap: 10px;
}

.register__testimonial-avatar {
  width: 32px;
  height: 32px;
  background: rgba(255,255,255,0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
}

.register__testimonial-author strong {
  display: block;
  font-size: 12px;
}

.register__testimonial-author span {
  font-size: 10px;
  opacity: 0.65;
}

/* ===== FORM WRAPPER (RIGHT) ===== */
.register__form-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 52px;
  background: #ffffff;
}

.register__form-container {
  width: 100%;
  max-width: 440px;
}

/* ===== STATES ===== */
.register__state {
  text-align: center;
  padding: 32px 0;
}

.register__state-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.register__state-icon svg { width: 34px; height: 34px; }

.register__state-icon--maintenance {
  background: #fffbeb;
  border: 2px solid #fde68a;
  color: #d97706;
}

.register__state-icon--locked {
  background: #fef2f2;
  border: 2px solid #fecaca;
  color: #dc2626;
}

.register__state h2 {
  color: #1a2b3d;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
}

.register__state p {
  color: #6b839c;
  font-size: 14px;
  margin-bottom: 4px;
}

.register__state-hint {
  font-size: 12px !important;
  color: #94a8bc !important;
}

.register__state-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 10px 22px;
  background: #0096ed;
  color: #fff;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.25s;
}

.register__state-link:hover {
  background: #007ad1;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0,150,237,0.3);
}

.register__state-link svg { width: 18px; height: 18px; transition: transform 0.25s; }
.register__state-link:hover svg { transform: translateX(3px); }

/* ===== HEADER ===== */
.register__header { margin-bottom: 24px; }

.register__invite-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #059669;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
}

.register__invite-badge svg { width: 15px; height: 15px; }

.register__title {
  color: #1a2b3d;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 4px;
}

.register__subtitle {
  color: #6b839c;
  font-size: 14px;
}

.register__subtitle strong { color: #0096ed; }

/* ===== PROGRESS ===== */
.register__progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
}

.register__progress-track {
  flex: 1;
  height: 5px;
  background: #e5eef6;
  border-radius: 100px;
  overflow: hidden;
}

.register__progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #0096ed, #40b4f7);
  border-radius: 100px;
  transition: width 0.5s;
}

.register__progress-text {
  font-size: 12px;
  color: #0096ed;
  font-weight: 700;
  min-width: 30px;
  text-align: right;
}

/* ===== FORM ===== */
.register__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* ===== FORM GROUP ===== */
.form-group { position: relative; }
.form-group__input-wrapper { position: relative; }

.form-group__input {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid #d4e4f1;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  background: #f8fbfe;
  color: #1a2b3d;
  transition: all 0.2s;
  outline: none;
}

.form-group__input::placeholder { color: transparent; }

.form-group__input:focus {
  border-color: #0096ed;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(0,150,237,0.08);
}

.form-group__input--with-icon { padding-right: 44px; }

/* Label */
.form-group__label {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8ca3b8;
  font-size: 14px;
  pointer-events: none;
  transition: all 0.2s;
  line-height: 1;
}

.form-group__input:focus ~ .form-group__label,
.form-group__input:not(:placeholder-shown) ~ .form-group__label,
.form-group--filled .form-group__label {
  top: 0;
  font-size: 11px;
  font-weight: 500;
  padding: 0 5px;
  left: 9px;
  color: #0096ed;
  background: #fff;
}

.form-group__input:not(:focus):not(:placeholder-shown) ~ .form-group__label {
  color: #6b839c;
  background: linear-gradient(180deg, #f8fbfe 45%, #fff 55%);
}

.form-group .required { color: #ef4444; }
.form-group .optional { color: #a8baca; font-size: 10px; }

/* Icon */
.form-group__icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #b0c4d6;
}

.form-group__icon svg { width: 18px; height: 18px; }

/* Toggle */
.form-group__toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: #b0c4d6;
  border-radius: 6px;
  transition: all 0.2s;
}

.form-group__toggle:hover { color: #0096ed; background: rgba(0,150,237,0.06); }
.form-group__toggle svg { width: 18px; height: 18px; }

/* States */
.form-group--readonly .form-group__input {
  background: #f0f5fa;
  color: #6b839c;
  border-color: #dfe9f2;
}

.form-group--disabled .form-group__input {
  background: #f5f7fa;
  color: #b5c3d1;
  cursor: not-allowed;
  pointer-events: none;
  border-color: #e5eaf0;
}

.form-group--disabled .form-group__label { color: #b5c3d1; }

.form-group--error .form-group__input { border-color: #fca5a5; background: #fff5f5; }
.form-group--success .form-group__input { border-color: #86efac; background: #f0fdf4; }

/* Status icon */
.form-group__status {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
}

.form-group__status svg { width: 18px; height: 18px; }
.form-group__status--success { color: #10b981; }
.form-group__status--error { color: #ef4444; }

/* Hints */
.form-group__hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 12px;
}

.form-group__hint svg { width: 13px; height: 13px; flex-shrink: 0; }
.form-group__hint--info { color: #0096ed; }
.form-group__hint--error { color: #ef4444; }

.checkbox--disabled { opacity: 0.4; pointer-events: none; }

/* ===== PASSWORD ===== */
.password-strength {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.password-strength__bars {
  display: flex;
  gap: 3px;
  flex: 1;
}

.password-strength__bar {
  height: 3px;
  flex: 1;
  background: #e5edf4;
  border-radius: 100px;
  transition: all 0.3s;
}

.password-strength__text {
  font-size: 11px;
  font-weight: 700;
  min-width: 55px;
  text-align: right;
}

.password-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.password-requirement {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #a8baca;
  transition: color 0.2s;
}

.password-requirement svg { width: 13px; height: 13px; opacity: 0.3; transition: all 0.2s; }
.password-requirement--met { color: #10b981; }
.password-requirement--met svg { opacity: 1; }

/* ===== ROLE CARD ===== */
.register__role-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #f0f8ff;
  border: 1.5px solid #b3daf7;
  border-radius: 10px;
}

.register__role-icon {
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #0096ed, #007ad1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(0,150,237,0.2);
}

.register__role-icon svg { width: 20px; height: 20px; }
.register__role-label { display: block; font-size: 11px; color: #6b839c; margin-bottom: 1px; }
.register__role-name { font-size: 15px; color: #1a2b3d; }

/* ===== CHECKBOX ===== */
.checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.checkbox__input { display: none; }

.checkbox__box {
  width: 20px;
  height: 20px;
  border: 1.5px solid #c4d5e3;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-top: 1px;
  background: #f8fbfe;
}

.checkbox__box svg {
  width: 12px;
  height: 12px;
  color: #fff;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s;
}

.checkbox__input:checked + .checkbox__box {
  background: #0096ed;
  border-color: #0096ed;
}

.checkbox__input:checked + .checkbox__box svg {
  opacity: 1;
  transform: scale(1);
}

.checkbox__text { font-size: 12px; color: #6b839c; line-height: 1.5; }

.checkbox__link {
  color: #0096ed;
  text-decoration: none;
  font-weight: 500;
}

.checkbox__link:hover { color: #007ad1; text-decoration: underline; }

/* ===== ERROR ===== */
.register__error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-size: 13px;
}

.register__error svg { width: 18px; height: 18px; flex-shrink: 0; }

.shake-enter-active { animation: shake 0.5s; }

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}

/* ===== SUBMIT ===== */
.register__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 48px;
  padding: 0 28px;
  background: #0096ed;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.25s;
  margin-top: 4px;
}

.register__submit:hover:not(:disabled) {
  background: #007ad1;
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0,150,237,0.25);
}

.register__submit:active:not(:disabled) { transform: translateY(0); }
.register__submit:disabled { opacity: 0.4; cursor: not-allowed; }

.register__submit-arrow {
  width: 18px;
  height: 18px;
  transition: transform 0.25s;
}

.register__submit:hover:not(:disabled) .register__submit-arrow {
  transform: translateX(3px);
}

.register__submit--loading .register__submit-content { opacity: 0.8; }

.register__submit-spinner { display: flex; gap: 4px; }

.register__submit-spinner div {
  width: 7px;
  height: 7px;
  background: #fff;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite;
}

.register__submit-spinner div:nth-child(1) { animation-delay: -0.32s; }
.register__submit-spinner div:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* ==========================================================
   RESPONSIVE
   Простой подход: только ширина branding и размеры шрифтов.
   Высота НИГДЕ не фиксирована — обрезания быть не может.
   ========================================================== */

/* 1601–1800 */
@media (min-width: 1601px) and (max-width: 1800px) {
  .register__branding { width: 42%; padding: 40px 38px; }
  .register__form-wrapper { padding: 40px 48px; }
  .register__logo { font-size: 22px; }
  .register__tagline { font-size: 14px; margin-bottom: 28px; }
  .register__stat-value { font-size: 20px; }
  .register__stats { margin-bottom: 28px; }
  .register__features { margin-bottom: 24px; }
}

/* 1367–1600 */
@media (min-width: 1367px) and (max-width: 1600px) {
  .register__branding { width: 40%; padding: 36px 32px; }
  .register__form-wrapper { padding: 36px 40px; }
  .register__logo { font-size: 20px; }
  .register__tagline { font-size: 13px; margin-bottom: 24px; }
  .register__stats { padding: 12px 0; margin-bottom: 24px; }
  .register__stat-value { font-size: 18px; }
  .register__stat-label { font-size: 9px; }
  .register__stat-divider { height: 26px; }
  .register__features { gap: 12px; margin-bottom: 20px; }
  .register__feature-icon { width: 36px; height: 36px; }
  .register__feature-icon svg { width: 17px; height: 17px; }
  .register__feature-text h3 { font-size: 13px; }
  .register__feature-text p { font-size: 11px; }
  .register__testimonial { padding: 14px 16px; }
  .register__testimonial-text { font-size: 12px; margin-bottom: 10px; }
  .register__title { font-size: 26px; }
  .register__form { gap: 14px; }
  .form-group__input { height: 44px; font-size: 13px; }
  .form-group__label { font-size: 13px; }
  .register__submit { height: 44px; font-size: 14px; }
}

/* 769–1366 */
@media (min-width: 769px) and (max-width: 1366px) {
  .register__branding { width: 36%; padding: 28px 24px; }
  .register__form-wrapper { padding: 28px 32px; }
  .register__form-container { max-width: 400px; }
  .register__logo { font-size: 18px; }
  .register__tagline { font-size: 12px; margin-bottom: 20px; line-height: 1.5; }
  .register__stats { padding: 10px 0; margin-bottom: 20px; }
  .register__stat-value { font-size: 16px; }
  .register__stat-label { font-size: 9px; }
  .register__stat-divider { height: 22px; }
  .register__features { gap: 10px; margin-bottom: 16px; }
  .register__feature { gap: 10px; }
  .register__feature-icon { width: 32px; height: 32px; border-radius: 8px; }
  .register__feature-icon svg { width: 15px; height: 15px; }
  .register__feature-text h3 { font-size: 12px; }
  .register__feature-text p { font-size: 10px; }
  .register__testimonial { padding: 12px 14px; }
  .register__testimonial-text { font-size: 11px; margin-bottom: 8px; }
  .register__testimonial-avatar { width: 28px; height: 28px; font-size: 10px; }
  .register__testimonial-author strong { font-size: 11px; }
  .register__testimonial-author span { font-size: 9px; }
  .register__header { margin-bottom: 18px; }
  .register__title { font-size: 22px; }
  .register__subtitle { font-size: 13px; }
  .register__progress { margin-bottom: 16px; }
  .register__form { gap: 12px; }
  .form-row { gap: 10px; }
  .form-group__input { height: 42px; font-size: 13px; border-radius: 8px; padding: 0 12px; }
  .form-group__label { font-size: 13px; left: 10px; }
  .form-group__input:focus ~ .form-group__label,
  .form-group__input:not(:placeholder-shown) ~ .form-group__label,
  .form-group--filled .form-group__label { font-size: 10px; left: 8px; }
  .register__submit { height: 42px; font-size: 14px; border-radius: 8px; }
  .checkbox__text { font-size: 11px; }
  .checkbox__box { width: 18px; height: 18px; }
  .checkbox__box svg { width: 11px; height: 11px; }
  .password-requirements { gap: 4px; }
  .password-requirement { font-size: 10px; }
  .password-requirement svg { width: 12px; height: 12px; }
  .password-strength__text { font-size: 10px; min-width: 50px; }
  .register__branding-ring--1 { width: 280px; height: 280px; }
  .register__branding-ring--2 { width: 180px; height: 180px; }
}

/* ≤768 — скрываем branding */
@media (max-width: 768px) {
  .register__layout { flex-direction: column; }
  .register__branding { display: none; }
  .register__form-wrapper { padding: 36px 28px; }
}

@media (max-width: 600px) {
  .register__form-wrapper { padding: 28px 20px; }
  .register__title { font-size: 24px; }
  .form-row { grid-template-columns: 1fr; }
  .register__progress-text { display: none; }
}

@media (max-width: 400px) {
  .register__form-wrapper { padding: 24px 16px; }
  .register__title { font-size: 22px; }
  .register__submit { height: 46px; }
}
</style>
